import { useCallback, useEffect, useMemo } from "react";

import { faSave } from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { Tooltip } from "@mui/material";
import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowPinned,
} from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Icon } from "src/base-components/Icon";
import { extracActionFormValues } from "src/cases/caseView/CaseAction";
import { ActionFormFieldGroup } from "src/cases/caseView/components/ActionFormFieldGroup";
import { FormField } from "src/cases/caseView/components/FormField";
import { HintedFieldRow } from "src/cases/caseView/components/HintedFieldRow";
import {
  AvailableCaseAction,
  useCaseActionFormSchema,
} from "src/cases/caseView/hooks";
import {
  CaseActionFormValues,
  toPartialSchema,
} from "src/cases/caseView/schema";
import { transformFormValuesToPayload } from "src/cases/caseView/utils";
import { isCaseCopilotEnabled } from "src/cases/copilot/isCaseCopilotEnabled";
import { useOpenCaseCopilot } from "src/cases/copilot/useCaseCopilot";
import { useCase, useSaveCaseActionForm } from "src/cases/queries/cases";
import { TaskIdExtractor } from "src/cases/queries/casesTypes";
import {
  useActionFieldHints,
  useApproveActionFieldHint,
  useRejectActionFieldHint,
  useRejectActionHint,
} from "src/cases/queries/hints";
import {
  CaseTemplateActionOutput,
  CaseTemplateFormFieldsInner,
  Value,
} from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

// For fields with active hints, preserve the previously saved values
// instead of saving the hint values
const excludeHintedFields = (
  currentValues: Record<string, unknown>,
  fieldHints: Record<string, unknown>,
  savedValues: Record<string, unknown>,
  templateFields: CaseTemplateFormFieldsInner[] | undefined,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(currentValues).map(([key, value]) =>
      key in fieldHints
        ? [
            key,
            savedValues[key] ??
              (templateFields?.find((f) => f.key === key)?.type ===
              "multi_select"
                ? []
                : null),
          ]
        : [key, value],
    ),
  );

type CaseActionFormData = {
  actionInTemplate: CaseTemplateActionOutput;
  selectedAction: AvailableCaseAction;
  disabled?: boolean;
  caseId: string;
};

export const CaseActionFormWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const { close, setPin } = useFloatingWindowsActions();
  const isPinned = useIsFloatingWindowPinned(id);
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const { actionInTemplate, selectedAction, disabled, caseId } =
    data as CaseActionFormData;

  const { data: caseData } = useCase(caseId);

  const liveAction = useMemo(() => {
    return caseData?.data.case_actions?.find((a) => a.id === selectedAction.id);
  }, [caseData, selectedAction.id]);

  const liveFormEtag = liveAction?.form?.meta?.etag;

  const copilotEnabled = isCaseCopilotEnabled(caseData);
  const { openCaseCopilot } = useOpenCaseCopilot();

  const { data: fieldHints } = useActionFieldHints(caseId, selectedAction?.id, {
    enabled: copilotEnabled,
  });

  const { mutate: approveActionFieldHint, isPending: isApprovingHint } =
    useApproveActionFieldHint();
  const { mutate: rejectActionFieldHint, isPending: isRejectingHint } =
    useRejectActionFieldHint();
  const { mutateAsync: rejectActionHint } = useRejectActionHint();

  const selectedActionSchema = useCaseActionFormSchema(actionInTemplate);
  const { mutateAsync: saveCaseActionForm, isPending: isSaving } =
    useSaveCaseActionForm(caseId);

  const partialFormValuesSchema = useMemo(
    () =>
      toPartialSchema<CaseActionFormValues>(selectedActionSchema, {
        skip: ["action_key"],
      }),
    [selectedActionSchema],
  );

  const defaultValues = useMemo(() => {
    const emptyFieldDefaults = Object.fromEntries(
      actionInTemplate.form?.fields?.map((field) => [
        field.key,
        field.type === "multi_select" ? [] : null,
      ]) ?? [],
    );
    return {
      ...emptyFieldDefaults,
      ...extracActionFormValues(liveAction ?? selectedAction),
      ...(fieldHints ?? {}),
    };
  }, [liveAction, selectedAction, fieldHints, actionInTemplate.form?.fields]);

  const form = useForm<CaseActionFormValues>({
    mode: "onBlur",
    defaultValues,
    resolver: yupResolver(partialFormValuesSchema),
  });

  useEffect(() => {
    form.reset(defaultValues, { keepDirtyValues: true });
  }, [defaultValues, form.reset]);

  const {
    getValues,
    formState: { isDirty, isValid },
  } = form;

  const hasHints = fieldHints != null && Object.keys(fieldHints).length > 0;

  // We can not rely on form state because we actually show hints in place,
  // so we need to know about the real form empty state
  const isFormEmpty = useMemo(() => {
    return (
      actionInTemplate.form?.fields?.every((field) => {
        const value = defaultValues[field.key];
        return field.type === "multi_select"
          ? Array.isArray(value) && value.length === 0
          : value == null;
      }) ?? true
    );
  }, [defaultValues, actionInTemplate.form?.fields]);

  const getSubmitValues = useCallback(() => {
    const values = getValues();
    if (!fieldHints) return values;

    const savedValues = extracActionFormValues(liveAction ?? selectedAction);
    return excludeHintedFields(
      values,
      fieldHints,
      savedValues,
      actionInTemplate.form?.fields,
    );
  }, [
    getValues,
    fieldHints,
    liveAction,
    selectedAction,
    actionInTemplate.form?.fields,
  ]);

  const handleSaveForm = useCallback(
    async (overrideValues?: Record<string, unknown>) => {
      const valuesToSave = overrideValues ?? getSubmitValues();
      form.reset(valuesToSave, { keepValues: true });

      try {
        const actionFormValues = transformFormValuesToPayload(
          partialFormValuesSchema,
          valuesToSave,
        );

        await saveCaseActionForm({
          actionFormValues,
          etag: liveFormEtag ?? selectedAction?.form.meta?.etag ?? 0,
          actionId: selectedAction?.id ?? "",
          actionFormId: selectedAction?.form.id ?? "",
        });

        toastActions.success({
          title: "Action form saved successfully",
        });

        return true;
      } catch (error) {
        form.reset(valuesToSave, { keepValues: true, keepDirty: true });
        logger.error("Error saving case action form:", error);

        if (isPreconditionError(error)) {
          toastActions.failure({
            title: "Failed to save action form",
            description:
              "The action form has been modified elsewhere. Please reload and try again.",
          });
          return false;
        }

        if (isAxiosError(error)) {
          toastActions.failure({
            title: "Failed to save action form",
            description: error.response?.data?.message,
          });
          return false;
        }

        toastActions.failure({
          title: "Failed to save action form",
          description: "An unexpected error occurred. Please try again.",
        });
        return false;
      }
    },
    [
      getSubmitValues,
      form,
      saveCaseActionForm,
      selectedAction,
      liveFormEtag,
      partialFormValuesSchema,
    ],
  );

  const handleClearForm = useCallback(async () => {
    const emptyValues: Record<string, unknown> = {
      action_key: selectedAction?.action_key,
      ...Object.fromEntries(
        actionInTemplate.form?.fields?.map((field) => [
          field.key,
          field.type === "multi_select" ? [] : null,
        ]) ?? [],
      ),
    };

    const previousValues = form.getValues();
    form.reset(emptyValues as CaseActionFormValues, { keepDirty: true });

    try {
      if (hasHints && caseId) {
        await rejectActionHint({
          caseId,
          actionKey: TaskIdExtractor.getStatusAndTaskKey(selectedAction.id),
        });
      }

      const success = await handleSaveForm(emptyValues);
      if (success) {
        form.reset(emptyValues);
      } else {
        form.reset(previousValues, { keepDirty: true });
      }
    } catch {
      form.reset(previousValues, { keepDirty: true });
      toastActions.failure({
        title: "Failed to clear form",
        description: "Could not reject hints. Please try again.",
      });
    }
  }, [
    form,
    actionInTemplate.form?.fields,
    handleSaveForm,
    hasHints,
    caseId,
    rejectActionHint,
    selectedAction,
  ]);

  const handleApproveField = useCallback(
    (fieldKey: string) => {
      const currentEtag = liveFormEtag ?? selectedAction?.form.meta?.etag;
      if (!caseId || currentEtag == null) return;
      const actionKey = TaskIdExtractor.getStatusAndTaskKey(selectedAction.id);
      approveActionFieldHint({
        caseId,
        actionKey,
        fieldKey,
        hintId: selectedAction.id,
        ifMatch: currentEtag.toString(),
        value: (fieldHints?.[fieldKey] ?? null) as Value | null,
      });
    },
    [caseId, liveFormEtag, selectedAction, fieldHints, approveActionFieldHint],
  );

  const handleRejectField = useCallback(
    (
      fieldKey: string,
      { clearValue = true }: { clearValue?: boolean } = {},
    ) => {
      if (!caseId || isRejectingHint) return;

      if (clearValue) {
        const field = actionInTemplate.form?.fields?.find(
          (f) => f.key === fieldKey,
        );
        const emptyValue = field?.type === "multi_select" ? [] : null;
        form.setValue(fieldKey, emptyValue, { shouldDirty: true });
      }

      rejectActionFieldHint({
        caseId,
        actionKey: TaskIdExtractor.getStatusAndTaskKey(selectedAction.id),
        fieldKey,
      });
    },
    [
      caseId,
      isRejectingHint,
      actionInTemplate.form?.fields,
      form,
      selectedAction,
      rejectActionFieldHint,
    ],
  );

  const canSave = isDirty && isValid && !isSaving;

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      dataLoc="case-action-form-window"
      headerAlignment="start"
      id={id}
      isPinned={isPinned}
      style={{
        inset: "auto auto 40px 0px",
        transform: position,
      }}
      title={
        <span className="font-inter-normal-13px text-gray-800">
          {actionInTemplate.display_name}
        </span>
      }
      titleRight={
        <Tooltip title="Save form">
          <span className="flex items-center justify-center">
            <Icon
              color={
                canSave
                  ? "text-indigo-500 hover:text-indigo-600"
                  : "text-gray-400"
              }
              dataLoc="case-action-form-save"
              disabled={!canSave}
              icon={faSave}
              size="xs"
              onClick={() => handleSaveForm()}
            />
          </span>
        </Tooltip>
      }
      draggable
      resizeable
      onClose={() => close(id)}
      onPin={() => setPin(id, !isPinned)}
    >
      <div className="decideScrollbar relative -mx-2 flex h-full flex-col justify-between">
        <FormProvider {...form}>
          <form>
            <div className="flex flex-col gap-y-3 p-4 pb-0">
              {actionInTemplate.form?.fields.map((field) => {
                const isHinted = fieldHints != null && field.key in fieldHints;
                const fieldContent = (
                  <ActionFormFieldGroup
                    errors={form.formState.errors}
                    field={field}
                  >
                    <FormField
                      disabled={disabled}
                      field={field}
                      hinted={copilotEnabled && isHinted}
                      onUserChange={
                        isHinted
                          ? () =>
                              handleRejectField(field.key, {
                                clearValue: false,
                              })
                          : undefined
                      }
                    />
                  </ActionFormFieldGroup>
                );

                if (!copilotEnabled) {
                  return <div key={field.key}>{fieldContent}</div>;
                }

                return (
                  <HintedFieldRow
                    key={field.key}
                    disabled={!!disabled || isApprovingHint || isRejectingHint}
                    fieldKey={field.key}
                    isHinted={isHinted}
                    onApprove={() => handleApproveField(field.key)}
                    onReject={() => handleRejectField(field.key)}
                    onRewriteWithCopilot={() =>
                      openCaseCopilot({ refLabel: field.display_name })
                    }
                  >
                    {fieldContent}
                  </HintedFieldRow>
                );
              })}
            </div>
          </form>
        </FormProvider>

        <div className="sticky bottom-0 mt-4 flex justify-between border-gray-100 border-t bg-white px-4 py-3 pb-2">
          <Button
            dataLoc="case-action-form-clear"
            disabled={isSaving || (isFormEmpty && !isDirty)}
            variant="secondary"
            onClick={handleClearForm}
          >
            Clear form
          </Button>
          <Button
            dataLoc="case-action-form-save-and-close"
            disabled={!canSave}
            loading={isSaving}
            onClick={async () => {
              const success = await handleSaveForm();
              if (success) close(id);
            }}
          >
            Save and Close
          </Button>
        </div>
      </div>
    </FloatingWindowInternal>
  );
};
